import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import InternalChat from '../../../components/InternalChat';
import ReferralBanner from '../../../components/ReferralBanner';
import BookingOverview from '../../superadmin/pages/BookingOverview';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiLogOut, FiBriefcase, FiCalendar, FiMessageSquare, FiAward, FiTrendingUp, FiClock, FiCheck, FiX, FiInfo, FiExternalLink, FiUser, FiCopy } = FiIcons;

const SellerDashboard = () => {
  const { user, logout } = useAuth();
  const { getSalesBySeller, guides, getUnreadCount, updateSellerAvailability } = useData();
  const navigate = useNavigate();
  const [salesData, setSalesData] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [isSavingHours, setIsSavingHours] = useState(false);

  const [availability, setAvailability] = useState({
    mandag: { start: '09:00', end: '16:00', active: true },
    tirsdag: { start: '09:00', end: '16:00', active: true },
    onsdag: { start: '09:00', end: '16:00', active: true },
    torsdag: { start: '09:00', end: '16:00', active: true },
    fredag: { start: '09:00', end: '16:00', active: true }
  });

  useEffect(() => {
    const targetId = user?.sellerId || user?.id;
    if (targetId && getSalesBySeller) {
      setSalesData(getSalesBySeller(targetId) || []);
    }
  }, [user, getSalesBySeller]);

  const unreadMessages = getUnreadCount(user?.id);
  const sellerGuides = guides ? guides.filter(g => (Array.isArray(g.categories) ? g.categories : [g.category]).includes('selgere')) : [];

  const handleSaveAvailability = async () => {
    setIsSavingHours(true);
    if (updateSellerAvailability) await updateSellerAvailability(user.id, availability);
    setTimeout(() => {
      setIsSavingHours(false);
      alert('Arbeidstid lagret!');
    }, 800);
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <nav className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 items-center">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-200">
                <SafeIcon icon={FiBriefcase} className="text-white text-xl" />
              </div>
              <div>
                <span className="font-black text-gray-900 text-lg uppercase tracking-tight">Selgerportal</span>
                <p className="text-xs text-gray-500 font-bold uppercase tracking-widest">{user?.name}</p>
              </div>
            </div>
            <button onClick={() => { logout(); navigate('/seller/login'); }} className="flex items-center gap-2 text-gray-500 hover:text-red-600 font-bold text-sm transition-colors bg-gray-50 px-4 py-2 rounded-xl border border-gray-100" >
              <SafeIcon icon={FiLogOut} /> Logg ut
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <ReferralBanner id={user?.sellerId || user?.id} type="seller" name={user?.name} />

        <div className="bg-white p-2 rounded-2xl shadow-sm border border-gray-100 flex gap-2 mb-10 overflow-x-auto no-scrollbar">
          {[
            { id: 'overview', label: 'Oversikt & Salg', icon: FiTrendingUp },
            { id: 'calendar', label: 'Kalender & Booking', icon: FiCalendar },
            { id: 'tools', label: 'Salgsverktøy', icon: FiAward },
            { id: 'chat', label: 'Meldinger', icon: FiMessageSquare, badge: unreadMessages }
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-3 px-6 py-3 rounded-xl text-sm font-black uppercase tracking-wider transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-blue-600 text-white shadow-xl' : 'text-gray-500 hover:bg-gray-50 hover:text-blue-600'}`} >
              <SafeIcon icon={tab.icon} /> {tab.label} {tab.badge > 0 && <span className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full ml-1">{tab.badge}</span>}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div key="overview" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-white p-8 rounded-[24px] shadow-sm border border-gray-100">
                  <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Totalt Salg</p>
                  <h3 className="text-4xl font-black text-gray-900">{salesData.length}</h3>
                </div>
                <div className="bg-white p-8 rounded-[24px] shadow-sm border border-gray-100">
                  <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Porteføljeverdi</p>
                  <h3 className="text-4xl font-black text-gray-900">{salesData.reduce((acc, curr) => acc + (curr.monthlyFee || 0), 0).toLocaleString()} kr</h3>
                </div>
                <div className="bg-white p-8 rounded-[24px] shadow-sm border border-gray-100">
                  <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Aktive Kunder</p>
                  <h3 className="text-4xl font-black text-blue-600">{salesData.filter(c => c.active).length}</h3>
                </div>
              </div>
              <div className="bg-white rounded-[32px] shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-8 py-6 border-b border-gray-50 bg-gray-50/50">
                  <h2 className="font-black text-gray-900 uppercase tracking-tight">Dine Kunder</h2>
                </div>
                <div className="divide-y divide-gray-50">
                  {salesData.length > 0 ? salesData.map((company) => (
                    <div key={company.id} className="px-8 py-6 hover:bg-blue-50/30 transition-colors flex justify-between items-center group">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400 group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors"><SafeIcon icon={FiBriefcase} /></div>
                        <div>
                          <h3 className="font-bold text-gray-900">{company.name}</h3>
                          <p className="text-xs text-gray-500 font-bold uppercase tracking-widest">{company.subscription}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-black text-gray-900">{company.monthlyFee} kr/mnd</div>
                        <span className={`text-[10px] font-black uppercase px-2 py-1 rounded mt-1 inline-block ${company.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{company.active ? 'Aktiv' : 'Inaktiv'}</span>
                      </div>
                    </div>
                  )) : <div className="p-20 text-center text-gray-400 font-bold uppercase tracking-widest">Ingen salg registrert enda.</div>}
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'calendar' && (
            <motion.div key="calendar" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                <div className="lg:col-span-3">
                  <BookingOverview forceAgentId={user.sellerId || user.id} role="seller" />
                </div>
                <div className="space-y-6">
                  <h3 className="text-lg font-black text-gray-900 uppercase tracking-tight">Tilgjengelighet</h3>
                  <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-xl">
                    <div className="space-y-4 mb-6">
                      {Object.keys(availability).map(day => (
                        <div key={day} className="flex items-center justify-between p-2">
                          <span className="text-xs font-bold uppercase text-gray-400 w-16">{day}</span>
                          <input type="text" value={availability[day].start} onChange={e => setAvailability({...availability, [day]: {...availability[day], start: e.target.value}})} className="w-14 text-center border-b text-xs font-mono" />
                          <span className="text-gray-300">-</span>
                          <input type="text" value={availability[day].end} onChange={e => setAvailability({...availability, [day]: {...availability[day], end: e.target.value}})} className="w-14 text-center border-b text-xs font-mono" />
                        </div>
                      ))}
                    </div>
                    <button onClick={handleSaveAvailability} className="w-full bg-blue-600 text-white py-3 rounded-xl font-black text-xs uppercase shadow-lg hover:bg-blue-700 transition-all">Lagre timer</button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'tools' && (
            <motion.div key="tools" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="grid md:grid-cols-2 gap-8">
              {sellerGuides.map((pitch) => (
                <div key={pitch.id} className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm hover:shadow-xl transition-all group">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="p-3 bg-orange-100 rounded-2xl text-orange-600 group-hover:scale-110 transition-transform"><SafeIcon icon={FiTrendingUp} className="text-xl" /></div>
                    <h4 className="font-black text-gray-900 uppercase tracking-tight">{pitch.title}</h4>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed italic border-l-4 border-gray-100 pl-4">{pitch.content}</p>
                </div>
              ))}
            </motion.div>
          )}

          {activeTab === 'chat' && (
            <motion.div key="chat" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <InternalChat currentUserType="seller" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SellerDashboard;